import {
  sharedProteinIntakeStep,
  sharedSleepQualityStep,
  sharedStressLevelStep,
} from '@/flow/shared/commonQuestions'
import type { Step } from '@/types'

// Helper to check user sex
const isMale = (ctx: { vars: Record<string, any> }) => ctx.vars.sexo === 'masculino'
const isFemale = (ctx: { vars: Record<string, any> }) => ctx.vars.sexo === 'feminino'

// Helper to check specific concerns
const hasConcern = (concern: string) => (ctx: { vars: Record<string, any> }) => {
  const concerns = ctx.vars.preocupacoes_cabelo
  return Array.isArray(concerns) && concerns.includes(concern)
}

// Helper to check if has family history
const hasFamilyHistory = (ctx: { vars: Record<string, any> }) =>
  ctx.vars.historico_familiar === 'sim'

/** Objective-specific steps: Hair Loss / Baldness */
export const hairLossSteps: Step[] = [
  // ========== INTRO ==========
  {
    id: 'intro_hair',
    type: 'message',
    content: 'Vamos entender melhor a saúde do seu cabelo para criar um protocolo personalizado.',
  },

  // ========== MAIN CONCERNS ==========
  {
    id: 'hair_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_cabelo',
    multiple: true,
    minSelections: 1,
    content: 'Quais são suas principais preocupações com o cabelo?',
    options: [
      { label: 'Queda de cabelo', value: 'queda' },
      { label: 'Afinamento dos fios', value: 'afinamento' },
      { label: 'Entradas / Recuo da linha capilar', value: 'entradas' },
      { label: 'Falhas no couro cabeludo', value: 'falhas' },
      { label: 'Cabelo sem volume', value: 'sem_volume' },
      { label: 'Couro cabeludo oleoso', value: 'oleosidade' },
      { label: 'Caspa / Dermatite seborreica', value: 'caspa' },
      { label: 'Cabelo quebradiço', value: 'quebradico' },
    ],
  },

  // ========== HAIR LOSS DETAILS ==========
  {
    id: 'hair_loss_duration',
    type: 'choice',
    inputKey: 'queda_duracao',
    content: 'Há quanto tempo você nota a queda de cabelo?',
    condition: hasConcern('queda'),
    options: [
      { label: 'Menos de 6 meses', value: 'menos_6_meses' },
      { label: '6 meses a 1 ano', value: '6_meses_1_ano' },
      { label: '1 a 3 anos', value: '1_3_anos' },
      { label: 'Mais de 3 anos', value: 'mais_3_anos' },
    ],
  },
  {
    id: 'hair_loss_intensity',
    type: 'choice',
    inputKey: 'queda_intensidade',
    content: 'Como você classificaria a intensidade da queda?',
    condition: hasConcern('queda'),
    options: [
      { label: 'Leve (alguns fios a mais que o normal)', value: 'leve' },
      { label: 'Moderada (bastante fios no travesseiro/chuveiro)', value: 'moderada' },
      { label: 'Severa (tufos de cabelo caindo)', value: 'severa' },
    ],
  },
  {
    id: 'hair_loss_pattern_male',
    type: 'choice',
    inputKey: 'padrao_queda',
    content: 'Onde você percebe mais a queda ou fragilidade?',
    condition: (ctx) => hasConcern('queda')(ctx) && isMale(ctx),
    options: [
      { label: 'Entradas (testa lateral)', value: 'entradas' },
      { label: 'Coroa (topo da cabeça)', value: 'coroa' },
      { label: 'Difusa (cabelo todo)', value: 'difusa' },
    ],
  },
  {
    id: 'hair_loss_pattern_female',
    type: 'choice',
    inputKey: 'padrao_queda',
    content: 'Onde você percebe mais a queda ou rarefação?',
    condition: (ctx) => hasConcern('queda')(ctx) && isFemale(ctx),
    options: [
      { label: 'Risca central (alargamento)', value: 'risca_central' },
      { label: 'Topo da cabeça', value: 'topo' },
      { label: 'Difusa (cabelo todo)', value: 'difusa' },
      { label: 'Frontal (linha do cabelo)', value: 'frontal' },
    ],
  },
  {
    id: 'hair_loss_pattern_neutral',
    type: 'choice',
    inputKey: 'padrao_queda',
    content: 'Onde você percebe mais a queda ou rarefação?',
    condition: (ctx) => hasConcern('queda')(ctx) && !isMale(ctx) && !isFemale(ctx),
    options: [
      { label: 'Entradas (testa lateral)', value: 'entradas' },
      { label: 'Topo da cabeça', value: 'topo' },
      { label: 'Difusa (cabelo todo)', value: 'difusa' },
      { label: 'Linha frontal', value: 'frontal' },
    ],
  },

  // ========== THINNING DETAILS ==========
  {
    id: 'thinning_when',
    type: 'choice',
    inputKey: 'afinamento_quando',
    content: 'Quando você notou o afinamento dos fios?',
    condition: hasConcern('afinamento'),
    options: [
      { label: 'Sempre tive fios finos', value: 'sempre' },
      { label: 'Começou recentemente', value: 'recente' },
      { label: 'Vem piorando aos poucos', value: 'progressivo' },
    ],
  },

  // ========== SCALP HEALTH ==========
  {
    id: 'scalp_oiliness',
    type: 'choice',
    inputKey: 'oleosidade_couro',
    content: 'Como está a oleosidade do seu couro cabeludo?',
    condition: hasConcern('oleosidade'),
    options: [
      { label: 'Normal', value: 'normal' },
      { label: 'Muito oleoso (lavo e fica oleoso no mesmo dia)', value: 'muito_oleoso' },
      { label: 'Oleoso (preciso lavar dia sim, dia não)', value: 'oleoso' },
    ],
  },
  {
    id: 'dandruff_type',
    type: 'choice',
    inputKey: 'caspa_tipo',
    content: 'Que tipo de caspa você tem?',
    condition: hasConcern('caspa'),
    options: [
      { label: 'Caspa seca (flocos brancos)', value: 'seca' },
      { label: 'Caspa oleosa (placas amareladas)', value: 'oleosa' },
      { label: 'Dermatite seborreica (vermelhidão e coceira)', value: 'dermatite' },
      { label: 'Não sei ao certo', value: 'nao_sei' },
    ],
  },
  {
    id: 'scalp_itching',
    type: 'choice',
    inputKey: 'coceira_couro',
    content: 'Você sente coceira ou irritação no couro cabeludo?',
    options: [
      { label: 'Sim, frequentemente', value: 'frequente' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Não', value: 'nao' },
    ],
  },

  // ========== FAMILY HISTORY ==========
  {
    id: 'family_history',
    type: 'choice',
    inputKey: 'historico_familiar',
    content: 'Existe histórico de alopecia / queda capilar na sua família?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
      { label: 'Não sei', value: 'nao_sei' },
    ],
  },
  {
    id: 'family_history_who',
    type: 'choice',
    inputKey: 'historico_familiar_quem',
    multiple: true,
    minSelections: 1,
    content: 'Quem da família tem ou teve alopecia / queda capilar?',
    condition: hasFamilyHistory,
    options: [
      { label: 'Pai', value: 'pai' },
      { label: 'Mãe', value: 'mae' },
      { label: 'Avô paterno', value: 'avo_paterno' },
      { label: 'Avô materno', value: 'avo_materno' },
      { label: 'Irmãos', value: 'irmaos' },
      { label: 'Tios', value: 'tios' },
    ],
  },

  // ========== TRIGGERS / CAUSES ==========
  // Female-specific triggers
  {
    id: 'possible_triggers_female',
    type: 'choice',
    inputKey: 'possiveis_gatilhos',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhum desses',
    content: 'Você passou por alguma dessas situações recentemente?',
    condition: isFemale,
    options: [
      { label: 'Estresse intenso', value: 'estresse' },
      { label: 'Dieta restritiva / Perda de peso rápida', value: 'dieta' },
      { label: 'Cirurgia ou doença grave', value: 'doenca' },
      { label: 'Parto (últimos 12 meses)', value: 'parto' },
      { label: 'Mudança ou parada de anticoncepcional', value: 'anticoncepcional' },
      { label: 'COVID-19', value: 'covid' },
      { label: 'Menopausa ou alterações hormonais', value: 'hormonal' },
      {
        label: 'Uso de medicações para emagrecer (Ozempic, Wegovy, etc)',
        value: 'medicacao_emagrecer',
      },
    ],
  },
  // Male-specific triggers
  {
    id: 'possible_triggers_male',
    type: 'choice',
    inputKey: 'possiveis_gatilhos',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhum desses',
    content: 'Você passou por alguma dessas situações recentemente?',
    condition: isMale,
    options: [
      { label: 'Estresse intenso', value: 'estresse' },
      { label: 'Dieta restritiva / Perda de peso rápida', value: 'dieta' },
      { label: 'Cirurgia ou doença grave', value: 'doenca' },
      { label: 'COVID-19', value: 'covid' },
      {
        label: 'Uso de medicações para emagrecer (Ozempic, Wegovy, etc)',
        value: 'medicacao_emagrecer',
      },
      { label: 'Uso de anabolizantes', value: 'anabolizantes' },
    ],
  },
  // Neutral triggers (for unspecified sex)
  {
    id: 'possible_triggers_neutral',
    type: 'choice',
    inputKey: 'possiveis_gatilhos',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhum desses',
    content: 'Você passou por alguma dessas situações recentemente?',
    condition: (ctx) => !isMale(ctx) && !isFemale(ctx),
    options: [
      { label: 'Estresse intenso', value: 'estresse' },
      { label: 'Dieta restritiva / Perda de peso rápida', value: 'dieta' },
      { label: 'Cirurgia ou doença grave', value: 'doenca' },
      { label: 'COVID-19', value: 'covid' },
      {
        label: 'Uso de medicações para emagrecer (Ozempic, Wegovy, etc)',
        value: 'medicacao_emagrecer',
      },
      { label: 'Alterações hormonais', value: 'hormonal' },
    ],
  },

  // ========== CURRENT TREATMENTS ==========
  {
    id: 'current_treatments',
    type: 'choice',
    inputKey: 'tratamentos_atuais',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não estou usando nada',
    content: 'Você já está usando algum tratamento para queda de cabelo?',
    options: [
      { label: 'Minoxidil', value: 'minoxidil' },
      { label: 'Finasterida / Dutasterida', value: 'finasterida' },
      { label: 'Shampoo anticaspa/antiqueda', value: 'shampoo' },
      { label: 'Suplementos (biotina, etc)', value: 'suplementos' },
      { label: 'Tratamento capilar em clínica', value: 'clinica' },
      { label: 'Transplante capilar', value: 'transplante' },

      { label: 'Outros', value: 'outros' },
    ],
  },
  {
    id: 'treatment_duration',
    type: 'choice',
    inputKey: 'tratamento_duracao',
    content: 'Há quanto tempo você usa esse tratamento?',
    condition: (ctx) => {
      const treatments = ctx.vars.tratamentos_atuais
      return Array.isArray(treatments) && treatments.length > 0
    },
    options: [
      { label: 'Menos de 3 meses', value: 'menos_3_meses' },
      { label: '3 a 6 meses', value: '3_6_meses' },
      { label: '6 meses a 1 ano', value: '6_meses_1_ano' },
      { label: 'Mais de 1 ano', value: 'mais_1_ano' },
    ],
  },
  {
    id: 'treatment_results',
    type: 'choice',
    inputKey: 'tratamento_resultados',
    content: 'Você notou algum resultado com o tratamento atual?',
    condition: (ctx) => {
      const treatments = ctx.vars.tratamentos_atuais
      return Array.isArray(treatments) && treatments.length > 0
    },
    options: [
      { label: 'Sim, está funcionando', value: 'funcionando' },
      { label: 'Um pouco, mas não o esperado', value: 'parcial' },
      { label: 'Não vi diferença', value: 'sem_diferenca' },
      { label: 'Piorou', value: 'piorou' },
    ],
  },

  // ========== HAIR CARE ROUTINE ==========
  {
    id: 'intro_routine',
    type: 'message',
    content: 'Agora algumas perguntas sobre seus hábitos de cuidado com o cabelo.',
  },
  {
    id: 'wash_frequency',
    type: 'choice',
    inputKey: 'frequencia_lavagem',
    content: 'Com que frequência você lava o cabelo?',
    options: [
      { label: 'Todos os dias', value: 'diario' },
      { label: 'Dia sim, dia não', value: 'alternado' },
      { label: '2-3 vezes por semana', value: '2_3_semana' },
      { label: '1 vez por semana ou menos', value: 'semanal' },
    ],
  },
  {
    id: 'hair_products',
    type: 'choice',
    inputKey: 'produtos_cabelo',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Só shampoo básico',
    content: 'Quais produtos você usa no cabelo?',
    options: [
      { label: 'Shampoo antiqueda', value: 'shampoo_antiqueda' },
      { label: 'Condicionador', value: 'condicionador' },
      { label: 'Máscara de tratamento', value: 'mascara' },
      { label: 'Leave-in / Sérum', value: 'leave_in' },
      { label: 'Tônico capilar', value: 'tonico' },
      { label: 'Óleo capilar', value: 'oleo' },
    ],
  },
  {
    id: 'heat_styling',
    type: 'choice',
    inputKey: 'uso_calor',
    content: 'Você usa secador, chapinha ou babyliss?',
    options: [
      { label: 'Sim, frequentemente', value: 'frequente' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Raramente', value: 'raramente' },
      { label: 'Nunca', value: 'nunca' },
    ],
  },
  {
    id: 'chemical_treatments',
    type: 'choice',
    inputKey: 'quimica_cabelo',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não faço química',
    content: 'Você faz algum tratamento químico no cabelo?',
    options: [
      { label: 'Tintura / Coloração', value: 'tintura' },
      { label: 'Descoloração', value: 'descoloracao' },
      { label: 'Progressiva / Alisamento', value: 'progressiva' },
      { label: 'Permanente', value: 'permanente' },
    ],
  },

  // ========== LIFESTYLE ==========
  {
    id: 'intro_lifestyle',
    type: 'message',
    content: 'Alguns hábitos podem influenciar a saúde do cabelo.',
  },
  {
    id: 'diet_quality',
    type: 'choice',
    inputKey: 'alimentacao',
    content: 'Como você avalia sua alimentação?',
    options: [
      { label: 'Equilibrada e variada', value: 'equilibrada' },
      { label: 'Poderia ser melhor', value: 'regular' },
      { label: 'Pobre em nutrientes', value: 'pobre' },
      { label: 'Faço dieta restritiva', value: 'restritiva' },
    ],
  },
  { ...sharedProteinIntakeStep },
  { ...sharedSleepQualityStep },
  { ...sharedStressLevelStep },
  {
    id: 'smoking',
    type: 'choice',
    inputKey: 'fumante',
    content: 'Você fuma?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
      { label: 'Parei recentemente', value: 'parou' },
    ],
  },

  // ========== HEALTH CONDITIONS ==========
  // Female-specific health conditions
  {
    id: 'health_conditions_female',
    type: 'choice',
    inputKey: 'condicoes_saude',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhuma dessas',
    content: 'Você tem alguma dessas condições?',
    condition: isFemale,
    options: [
      { label: 'Anemia', value: 'anemia' },
      { label: 'Problemas de tireoide', value: 'tireoide' },
      { label: 'Síndrome do ovário policístico (SOP)', value: 'sop' },
      { label: 'Diabetes', value: 'diabetes' },
      { label: 'Deficiência de vitaminas', value: 'vitaminas' },
      { label: 'Alopecia areata (falhas redondas)', value: 'alopecia_areata' },
      { label: 'Endometriose', value: 'endometriose' },
    ],
  },
  // Male and neutral health conditions
  {
    id: 'health_conditions_other',
    type: 'choice',
    inputKey: 'condicoes_saude',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhuma dessas',
    content: 'Você tem alguma dessas condições?',
    condition: (ctx) => !isFemale(ctx),
    options: [
      { label: 'Anemia', value: 'anemia' },
      { label: 'Problemas de tireoide', value: 'tireoide' },
      { label: 'Diabetes', value: 'diabetes' },
      { label: 'Deficiência de vitaminas', value: 'vitaminas' },
      { label: 'Alopecia areata (falhas redondas)', value: 'alopecia_areata' },
    ],
  },

  // ========== GOALS ==========
  {
    id: 'main_goals',
    type: 'choice',
    inputKey: 'objetivos_cabelo',
    multiple: true,
    minSelections: 1,
    content: 'Quais são seus principais objetivos?',
    options: [
      { label: 'Reduzir a queda', value: 'reduzir_queda' },
      { label: 'Engrossar os fios', value: 'engrossar' },
      { label: 'Recuperar volume', value: 'volume' },
      { label: 'Fortalecer o cabelo', value: 'fortalecer' },
      { label: 'Tratar o couro cabeludo', value: 'couro_cabeludo' },
      { label: 'Estimular crescimento', value: 'crescimento' },
    ],
  },
]
